import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { API } from '../data/API/API';
import CustomerTable from '../components/CustomerTable/CustomerTable';
import InvoiceBarChart from '../components/InvoiceBarChart/InvoiceBarChart';
import { CustomerTableFilterButton } from '../components/FilterButton/CustomerTableFilterButton';
import MonthPicker from '../components/DatePicker/MonthPicker';
import FilterForm from '../components/FilterForm/FilterForm';
import ThresholdSlider from '../components/ThresholdSlider/ThresholdSlider';
import styles from './ReviewSupplierPage.module.scss';
import { format, parseISO } from 'date-fns';
import { sv } from 'date-fns/locale';
import { CSSTransition } from 'react-transition-group';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faSearch, faExclamationCircle, faTimesCircle, faCheckCircle, faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { Modal, Button, Spinner } from 'react-bootstrap';
import Slider from 'rc-slider';
import AValueListModal from '../components/AValueListModal/AValueListModal';
import 'rc-slider/assets/index.css';
import ReviewListModal from '../components/ReviewListModal/ReviewListModal';
import AValueInfoModal from '../components/AValueInfoModal/AValueInfoModal';

const ReviewSupplierPage: React.FC = () => {
    const { orgNr } = useParams<{ orgNr?: string }>();
    const location = useLocation();
    const { aValue = "N/A", staticAValue = "N/A", period = null, isLoading: initialLoading = true } = location.state || {};
    const [aVardeInfoModel, setAvardeInfoModal] = useState(false);

    const [invoiceData, setInvoiceData] = useState<any[]>([]);
    const [diffData, setDiffData] = useState<any[]>([]);
    const [loading, setLoading] = useState(initialLoading);
    const [error, setError] = useState<string | null>(null);
    const [selectedDate, setSelectedDate] = useState<Date | null>(period ? parseISO(period) : null);
    const [showFilters, setShowFilters] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [sliderValues, setSliderValues] = useState<[number, number]>([30, 50]);
    const [liveSliderValues, setLiveSliderValues] = useState<[number, number]>([30, 50]);
    const [saveStatus, setSaveStatus] = useState<string | null>(null);
    const [thresholdValues, setThresholdValues] = useState<{ LimitYellow: number; LimitRed: number } | null>(null);
    const [isAValueListModalOpen, setIsAValueListModalOpen] = useState(false);
    const [supplierName, setSupplierName] = useState<string>("Okänd Leverantör");
    const [mostRecentReviewDate, setMostRecentReviewDate] = useState<string | null>(null);
    const navigate = useNavigate();

    const [selectedInvoiceNumber, setSelectedInvoiceNumber] = useState<string | null>(null);
    const [selectedCustomerOrgId, setSelectedCustomerOrgId] = useState<string | null>(null);
    const [isReviewListModalOpen, setIsReviewListModalOpen] = useState(false);
    const [selectedContractId, setSelectedContractId] = useState<string | null>(null);
    const formattedDate = selectedDate ? format(new Date(selectedDate), 'yyyy MMMM', { locale: sv }) : 'Månadens';
    const capitalizedDate = formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);

    const fetchData = useCallback(async (orgNr: string, date: Date) => {
        setLoading(true);
        await new Promise(resolve => setTimeout(resolve, 50));

        if (orgNr && date) {
            try {
                const formattedDate = format(date, 'yyyy-MM');
                const fullSupplierData = await API.Suppliers.GetFullSupplierData(orgNr, formattedDate);

                setSupplierName(fullSupplierData.supplierName || "Okänd Leverantör");
                setInvoiceData(fullSupplierData.invoices || []);
                setDiffData(fullSupplierData.aValue !== undefined && fullSupplierData.aValue !== null ? [fullSupplierData.aValue] : []);

                // Set default values for thresholds if they are null or undefined
                setThresholdValues({
                    LimitYellow: fullSupplierData.limitYellow !== null && fullSupplierData.limitYellow !== undefined
                        ? fullSupplierData.limitYellow
                        : 20, // Default to 20 if not set
                    LimitRed: fullSupplierData.limitRed !== null && fullSupplierData.limitRed !== undefined
                        ? fullSupplierData.limitRed
                        : 40, // Default to 40 if not set
                });

                setMostRecentReviewDate(fullSupplierData.mostRecentReviewDate || 'N/A');
            } catch (error) {
                setError('Misslyckades med att hämta data');
            } finally {
                setLoading(false);
            }
        } else {
            setLoading(false);
        }
    }, []);


    useEffect(() => {
        if (orgNr && selectedDate) {
            fetchData(orgNr, selectedDate);
        }
    }, [orgNr, selectedDate, fetchData]);

    useEffect(() => {
        if (isModalOpen) {
            setLiveSliderValues(sliderValues);
        }
    }, [isModalOpen, sliderValues]);

    const handleContactMembersClick = () => {
        navigate(`/supplier/${orgNr}/contact-members`, {
            state: {
                orgNr: orgNr,
                supplierName: supplierName,
                selectedDate: selectedDate ? format(selectedDate, 'yyyy-MM') : null,
            }
        });
    };

    const filteredInvoiceData = useMemo(() => {
        if (loading) return [];
        const filteredByDate = selectedDate
            ? invoiceData.filter((invoice) => {
                const invoiceDate = format(new Date(invoice.billingDate), 'yyyy-MM');
                const selectedMonth = format(selectedDate, 'yyyy-MM');
                return invoiceDate === selectedMonth;
            })
            : invoiceData;

        return filteredByDate.filter((invoice) => {
            const matchesCustomer = selectedCustomerOrgId
                ? `${invoice.customerName} - ${invoice.customerOrgId}` === selectedCustomerOrgId
                : true;
            const matchesContract = selectedContractId ? invoice.contractId === selectedContractId : true;
            const matchesInvoice = selectedInvoiceNumber ? invoice.invoiceNumber === selectedInvoiceNumber : true;
            return matchesCustomer && matchesContract && matchesInvoice;
        });
    }, [invoiceData, selectedDate, selectedCustomerOrgId, selectedContractId, selectedInvoiceNumber, loading]);

    const onDateChange = (date: Date | null) => {
        setSelectedDate(date);
        setSelectedCustomerOrgId(null);
        setSelectedContractId(null);
        setSelectedInvoiceNumber(null);
    };

    const handleFilterClick = () => {
        setShowFilters(!showFilters);
    };

    const handleBarClick = (date: Date) => {
        setSelectedDate(date);
    };

    const handleEditClick = () => {
        if (thresholdValues) {
            setSliderValues([thresholdValues.LimitYellow, thresholdValues.LimitRed]);
        }
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSaveStatus(null);
    };

    const handleAValueListModalOpen = () => {
        setIsAValueListModalOpen(true);
    };

    const handleOpenReviewList = () => {
        setIsReviewListModalOpen(true);
    };

    const getUniqueValues = (array: any[]) => Array.from(new Set(array));

    const visibleInvoiceNumbers = getUniqueValues(
        filteredInvoiceData.map((invoice) => invoice.invoiceNumber)
    );

    const visibleCustomerOrgIds = getUniqueValues(
        filteredInvoiceData.map((invoice) => `${invoice.customerName} - ${invoice.customerOrgId}`)
    );

    const visibleContractIds = getUniqueValues(
        filteredInvoiceData.map((invoice) => invoice.contractId)
    );

    const worstDiffByMax24ForSelectedMonth = diffData.length > 0 ? diffData[0] : null;

    const filterOptions = [
        {
            type: 'Select' as const,
            key: 'invoiceNumber',
            label: 'Fakturanummer',
            options: visibleInvoiceNumbers.map((num) => ({ value: num, label: num })),
            setValue: (value: string | null) => setSelectedInvoiceNumber(value),
            value: selectedInvoiceNumber,
            placeholder: 'Välj eller ange fakturanummer',
        },
        {
            type: 'Select' as const,
            key: 'customerOrgId',
            label: 'Kund Org.nr',
            options: visibleCustomerOrgIds.map((id) => ({ value: id, label: id })),
            setValue: (value: string | null) => setSelectedCustomerOrgId(value),
            value: selectedCustomerOrgId,
            placeholder: 'Välj eller ange kund org.nr',
        },
        {
            type: 'Select' as const,
            key: 'contractId',
            label: 'Kontrakt-ID',
            options: visibleContractIds.map((id) => ({ value: id, label: id })),
            setValue: (value: string | null) => setSelectedContractId(value),
            value: selectedContractId,
            placeholder: 'Välj eller ange kontrakt-ID',
        },
    ];

    return (
        <div className={styles.reviewSuppliersContainer}>
            <div className={styles.supplierInfo}>
                <h1>{supplierName} - {orgNr}</h1>
                <div className={styles.buttonGroup}>
                    <MonthPicker
                        selectedDate={selectedDate}
                        onDateChange={onDateChange}
                        loading={loading}
                    />
                    <button className="btn" onClick={handleContactMembersClick} disabled={loading}>
                        Kontakta medlemmar
                    </button>
                </div>
            </div>

            <div className={styles.chartAndTextboxes}>
                <div className={styles.chartWrapper}>
                    <InvoiceBarChart
                        invoiceData={invoiceData}
                        onBarClick={loading ? () => { } : handleBarClick}
                        selectedDate={selectedDate}
                        loading={loading}
                    />
                </div>

                <div className={styles.textboxes}>
                    <div className={styles.textbox}>
                        <button className={styles.editButton} onClick={handleEditClick} disabled={loading}>
                            <FontAwesomeIcon icon={faEdit} />
                        </button>

                        <h2 className={styles.aValue}>
                            {thresholdValues && thresholdValues.LimitYellow !== undefined && thresholdValues.LimitRed !== undefined
                                ? (
                                    <div className={styles.valueContainer}>
                                        <FontAwesomeIcon icon={faExclamationCircle} style={{ color: '#F5A32D', marginRight: '1px' }} /> {/* Yellow */}
                                        <span>{thresholdValues.LimitYellow}%</span>
                                        <FontAwesomeIcon icon={faTimesCircle} style={{ color: '#DC143C', marginRight: '1px' }} /> {/* Red */}
                                        <span>{thresholdValues.LimitRed}%</span>
                                    </div>
                                )
                                : "N/A"}
                        </h2>
                        <p className={styles.textboxParagraph}>Gränsvärden</p>
                    </div>

                    <div className={styles.textbox}>
                        <button className={styles.editButton} onClick={handleAValueListModalOpen} disabled={loading}>
                            <FontAwesomeIcon icon={faSearch} />
                        </button>
                        <button className={styles.aVardeInfoButton} onClick={() => { setAvardeInfoModal(true) }} disabled={loading}>
                            <FontAwesomeIcon icon={faCircleInfo} />
                        </button>
                        <h2 className={styles.aValue}>
                            {worstDiffByMax24ForSelectedMonth !== null && worstDiffByMax24ForSelectedMonth !== undefined ? (
                                <div className={styles.valueContainer}>
                                    {(() => {
                                        const aValue = worstDiffByMax24ForSelectedMonth;
                                        let color = '#07A235';
                                        let icon = faCheckCircle;

                                        if (thresholdValues && aValue > thresholdValues.LimitRed) {
                                            color = '#DC143C';
                                            icon = faTimesCircle;
                                        } else if (thresholdValues && aValue > thresholdValues.LimitYellow) {
                                            color = '#F5A32D';
                                            icon = faExclamationCircle;
                                        }

                                        return (
                                            <>
                                                <FontAwesomeIcon icon={icon} style={{ color, marginRight: '1px' }} />
                                                <span>{typeof aValue === 'number' ? aValue.toFixed(2) : 'N/A'}%</span>
                                            </>
                                        );
                                    })()}
                                </div>
                            ) : 'N/A'}
                        </h2>
                        <p className={styles.textboxParagraph}>{capitalizedDate} A.Värde</p>
                    </div>
                    <div className={styles.textbox}>
                        <button className={styles.editButton} onClick={handleOpenReviewList} disabled={loading}>
                            <FontAwesomeIcon icon={faSearch} />
                        </button>
                        <h2 className={styles.aValue}>
                            <div className={styles.valueContainer}>
                                {mostRecentReviewDate ? mostRecentReviewDate : 'N/A'}
                            </div>
                        </h2>
                        <p className={styles.textboxParagraph}>Senast granskad</p>
                    </div>
                </div>
            </div>

            <div className={styles.titleAndFilter}>
                <h3>Fakturor</h3>
                <CustomerTableFilterButton onFilterClick={handleFilterClick} />
            </div>

            <CSSTransition in={showFilters} timeout={300} classNames="filter-collapse" unmountOnExit>
                <div className={styles.filterContainer}>
                    <FilterForm filters={filterOptions} />
                </div>
            </CSSTransition>

            <CustomerTable
                invoiceData={filteredInvoiceData}
                isLoading={loading}
            />

            <ThresholdSlider
                orgNr={orgNr || ''}
                initialValues={sliderValues}
                updateThresholdValues={(newValues) => {
                    setThresholdValues(newValues);
                }}
                onClose={closeModal}
                isOpen={isModalOpen}
            />

            <AValueListModal
                orgNr={orgNr || ''}
                isOpen={isAValueListModalOpen}
                onClose={() => setIsAValueListModalOpen(false)}
                thresholdRed={thresholdValues?.LimitRed}
                thresholdYellow={thresholdValues?.LimitYellow}
                selectedDate={selectedDate || undefined}
            />

            <ReviewListModal
                orgNr={orgNr || ''}
                isOpen={isReviewListModalOpen}
                onClose={() => setIsReviewListModalOpen(false)}
            />

            {
                aVardeInfoModel &&
                <AValueInfoModal onDismiss={() => setAvardeInfoModal(false)} />
            }
        </div>
    );
};

export default ReviewSupplierPage;
